import * as Cesium from 'cesium';
import { haversineKm } from '../data/enrichment.js';

/**
 * Home-Alarm — meldet TIEFFLÜGE über dem gesetzten Zuhause.
 *
 * Bewusst nur "tiefe" Überflüge: Reiseflug hoch am Himmel soll den Alarm
 * NICHT auslösen, sondern nur Maschinen, die niedrig direkt über den
 * Alarm-Radius (siehe homeState) hinwegziehen.
 *
 * Darstellung (Dynamic-Island-Stil): Statt eines separaten Buttons verwandelt
 * sich die bestehende Zeitleiste unten in einen kompakten Alarm-Zustand. Betritt
 * ein Tiefflug die Zone, wird er sofort selektiert (Detailansicht öffnet sich)
 * und die Zeitleiste zeigt ihn als "Quick Return": Klickt man zwischendurch auf
 * der Karte herum oder wählt ein anderes Flugzeug, holt ein Tipp auf die
 * Zeitleiste den Alarm-Flug samt Detailansicht zurück. Verlässt der Flug die
 * Zone, animiert die Zeitleiste zurück in den Normalzustand.
 *
 * Mehrere gleichzeitige Tiefflüge: Die NÄCHSTE Maschine erhält die verwandelte
 * Zeitleiste (primär). Jede weitere bekommt eine eigene orange Pille, direkt
 * darüber gestapelt (nächste unten). Ein Tipp auf eine Pille selektiert den
 * zugehörigen Flug → dessen Detailkarte wird ebenfalls orange hinterlegt.
 *
 * Höhengrenze = Höhe ÜBER GRUND am Zuhause (AGL). Nach dem ersten Ausprobieren
 * hier bei Bedarf anpassen: kleiner = nur wirklich tiefe Überflüge.
 */
const ALARM_MAX_AGL_M = 4000;

// SVG-Inhalt einer gestapelten Zusatz-Pille (identisch zur Zeitleisten-Pille):
// Flugzeug-Icon · Titel/Sub · Return-Chevron.
const PILL_INNER_HTML = `
  <svg class="timeline-alarm-plane" viewBox="0 0 64 64" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M31.2 2.6C31.5 2.2 31.7 2 32 2c.3 0 .5.2.8.6 1.2 1.6 2 4.3 2 7.2v8.1l23.5 14.6c.5.3.7.8.7 1.3v4c0 .6-.6 1-1.1.8L34.7 32l-.9 15.1 7.4 5.6c.3.2.4.5.4.8v3.1c0 .6-.5 1-1.1.8L32 55.2l-8.5 2.2c-.6.2-1.1-.2-1.1-.8v-3.1c0-.3.1-.6.4-.8l7.4-5.6-.9-15.1-23.2 6.6c-.5.2-1.1-.2-1.1-.8v-4c0-.5.2-1 .7-1.3L29.2 17.9V9.8c0-2.9.8-5.6 2-7.2z"/></svg>
  <span class="timeline-alarm-text">
    <span class="timeline-alarm-title"></span>
    <span class="timeline-alarm-sub"></span>
  </span>
  <span class="timeline-alarm-return" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="16" height="16"><path fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" d="M9 6l6 6-6 6"/></svg>
  </span>`;

export function setupHomeAlarm(viewer, flightStore, flightManager, homeState) {
  const timeline = document.getElementById('timeline');
  const alarmBtn = document.getElementById('timelineAlarm');
  const titleEl = document.getElementById('timelineAlarmTitle');
  const subEl = document.getElementById('timelineAlarmSub');
  const infoPanel = document.getElementById('infoPanel');
  if (!timeline || !alarmBtn || !titleEl || !subEl) return;

  // Container für die gestapelten Zusatz-Pillen (2. … n-ter Flug), sitzt über
  // der Zeitleiste. Position wird per JS an die Zeitleiste angeheftet, da deren
  // unterer Rand (Info-Panel, Safe-Area) dynamisch ist.
  const stack = document.createElement('div');
  stack.id = 'homeAlarmStack';
  timeline.insertAdjacentElement('afterend', stack);

  // Aktueller Alarm-Zustand
  let primaryId = null;                 // nächster Flug → verwandelte Zeitleiste
  let alarmIds = new Set();             // alle gemeldeten Flug-IDs (für Highlight)
  const pillMap = new Map();            // id → { btn, titleEl, subEl } (Zusatz-Pillen)

  // Geländehöhe am Zuhause (falls Terrain geladen), sonst 0 → dann ist die
  // Grenze effektiv über Meereshöhe. Wird pro Auswertung frisch gelesen,
  // da die Kacheln evtl. erst nach und nach nachladen.
  function homeGroundHeight(home) {
    const h = viewer.scene.globe.getHeight(Cesium.Cartographic.fromDegrees(home.lon, home.lat));
    return Number.isFinite(h) ? h : 0;
  }

  function evaluate() {
    if (!homeState.hasHome()) { hide(); return; }

    const home = homeState.location;
    const radiusKm = homeState.radiusKm;
    const ground = homeGroundHeight(home);
    const maxAlt = ground + ALARM_MAX_AGL_M;

    // ALLE qualifizierenden Flüge sammeln (fliegend, tief, im Radius)
    const matches = [];
    for (const flight of flightStore.getAllFlights()) {
      if (flight.gone || flight.onGround) continue;
      const alt = flight.position?.altitude;
      if (!Number.isFinite(alt) || alt > maxAlt) continue; // zu hoch → kein Tiefflug

      const distKm = haversineKm(home, { lat: flight.position.lat, lon: flight.position.lon });
      if (distKm > radiusKm) continue;

      matches.push({ flight, distKm, agl: alt - ground });
    }

    // Nächster zuerst → primär (Zeitleiste), Rest gestapelt darüber
    matches.sort((a, b) => a.distKm - b.distKm);

    if (matches.length) show(matches); else hide();
  }

  function formatSub(distKm, agl) {
    const kmTxt = distKm.toLocaleString('de-DE', {
      minimumFractionDigits: 1, maximumFractionDigits: 1
    });
    // Höhe über Grund auf 10 m gerundet, nie negativ (Datenrauschen)
    const aglRounded = Math.max(0, Math.round(agl / 10) * 10);
    return `${kmTxt} km · ${aglRounded.toLocaleString('de-DE')} m über Grund`;
  }

  function labelFor(flight) {
    return flight.callsign || flight.id || 'Überflug';
  }

  function show(matches) {
    const primary = matches[0];
    const prevPrimary = primaryId;
    primaryId = primary.flight.id;
    alarmIds = new Set(matches.map((m) => m.flight.id));

    // Primärer Flug → verwandelte Zeitleiste
    titleEl.textContent = labelFor(primary.flight);
    subEl.textContent = formatSub(primary.distKm, primary.agl);
    timeline.classList.add('alarm');

    // Weitere Flüge → gestapelte Pillen darüber
    renderStack(matches.slice(1));
    positionStack();

    // Neuer nächster Tiefflug → sofort selektieren + Detailansicht öffnen. Nicht,
    // wenn der Nutzer gerade schon EINEN der Alarm-Flüge betrachtet (sonst würde
    // ein Poll-Update ihn beim Durchklicken der Pillen ständig wieder wegziehen).
    const selId = viewer.selectedEntity?.flightData?.id;
    if (primaryId !== prevPrimary && !alarmIds.has(selId)) selectFlight(primaryId);

    // Detailpanel + aktive Pille orange abheben
    syncPanelHighlight();
  }

  function hide() {
    if (primaryId === null && !timeline.classList.contains('alarm')) return;
    primaryId = null;
    alarmIds = new Set();
    // Zurück in den Normalzustand animieren
    timeline.classList.remove('alarm', 'is-selected');
    renderStack([]);
    // Kein Alarm-Flug mehr → Panel-Hervorhebung zurücknehmen
    syncPanelHighlight();
  }

  // Zusatz-Pillen (Flüge außer dem primären) abgleichen: bestehende in-place
  // aktualisieren (kein Neuaufbau → keine Animations-/Fokus-Sprünge pro Poll),
  // neue anlegen, verschwundene entfernen. Reihenfolge = Distanz (nächster zuerst);
  // der Container ist column-reverse, dadurch sitzt der nächste unten an der
  // Zeitleiste.
  function renderStack(extras) {
    const seen = new Set();
    for (const item of extras) {
      const id = item.flight.id;
      seen.add(id);
      let entry = pillMap.get(id);
      if (!entry) {
        entry = createPill(id);
        pillMap.set(id, entry);
      }
      entry.titleEl.textContent = labelFor(item.flight);
      entry.subEl.textContent = formatSub(item.distKm, item.agl);
      stack.appendChild(entry.btn); // ans Ende → erzwingt Distanz-Reihenfolge
    }
    for (const [id, entry] of pillMap) {
      if (!seen.has(id)) {
        entry.btn.remove();
        pillMap.delete(id);
      }
    }
  }

  function createPill(id) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'home-alarm-pill';
    btn.setAttribute('aria-label', 'Weiterer Tiefflug über Zuhause – Detailansicht öffnen');
    btn.innerHTML = PILL_INNER_HTML;
    btn.addEventListener('click', () => selectFlight(id));
    return {
      btn,
      titleEl: btn.querySelector('.timeline-alarm-title'),
      subEl: btn.querySelector('.timeline-alarm-sub'),
    };
  }

  // Stack an die Oberkante der Zeitleiste anheften (deren unterer Rand variiert
  // mit Info-Panel / Safe-Area). Links/Breite von der Zeitleiste übernehmen, damit
  // die Pillen exakt darüber ausgerichtet sind (unabhängig vom Breakpoint).
  function positionStack() {
    if (!stack.childElementCount) return;
    const r = timeline.getBoundingClientRect();
    stack.style.left = `${r.left}px`;
    stack.style.width = `${r.width}px`;
    stack.style.bottom = `${window.innerHeight - r.top + 8}px`;
  }

  // Solange das Detailpanel EINEN der gemeldeten Tiefflüge zeigt, wird es
  // orange-transparent hinterlegt (Abhebung zum normalen weißen Flugzeug-Panel).
  // Zusätzlich die zugehörige Pille (oder die Zeitleiste) als "aktiv" markieren.
  function syncPanelHighlight() {
    const selId = viewer.selectedEntity?.flightData?.id;
    const showsAlarm = selId != null && alarmIds.has(selId);
    infoPanel?.classList.toggle('home-alarm-flight', showsAlarm);

    timeline.classList.toggle('is-selected', selId != null && selId === primaryId);
    for (const [id, entry] of pillMap) {
      entry.btn.classList.toggle('is-selected', id === selId);
    }
  }

  // Flug auswählen → Info-Panel öffnen (dieselbe Auswahl wie ein Tipp aufs
  // Flugzeug; main.js reagiert auf selectedEntityChanged und zeigt das Panel).
  // Zugleich der Quick Return: holt einen Alarm-Flug nach einem Umweg zurück.
  function selectFlight(id) {
    if (!id) return;
    const entity = flightManager.entities.get(id);
    if (entity) viewer.selectedEntity = entity;
  }

  // Tipp auf die transformierte Zeitleiste → zurück zum primären Alarm-Flug
  alarmBtn.addEventListener('click', () => selectFlight(primaryId));

  // Wechselt die Auswahl (anderes Flugzeug/andere Pille antippen oder Quick
  // Return), die orange Hervorhebung passend mitziehen.
  viewer.selectedEntityChanged.addEventListener(syncPanelHighlight);

  // Layout-Verschiebungen (Zeitleiste dockt an Info-Panel an, Fenstergröße) →
  // Stack neu anheften.
  window.addEventListener('resize', positionStack);
  timeline.addEventListener('transitionend', positionStack);

  // Bei jedem Daten-Poll neu prüfen, außerdem bei Zuhause-/Radius-Änderung
  flightStore.on('update', evaluate);
  homeState.on('change', evaluate);
}
